import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ArrowDown, ArrowUp, ChevronsUpDown, Info } from 'lucide-react';
import { deliveryQualityService } from 'services/deliveryQualityService';
import { Card } from '../../../../components/ui/Card';
import { Input } from '../../../../components/ui/Input';
import { Badge } from '../../../../components/ui/Badge';
import { Modal } from '../../../../components/ui/Modal';
import { Skeleton } from '../../../../components/ui/Skeleton';
import { Table, THead, TBody, TR, TH, TD } from '../../../../components/ui/Table';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import PackageAiDetailModal from '../components/PackageAiDetailModal';
import DriverPackagesBuckets from '../components/DriverPackagesBuckets';
import { CardTableSkeleton } from './TabSkeletons';
import { labelForCriterion } from '../utils/criteriaLabels';
import { formatPercent as fmtPct } from '../../../../utils/formatters';

const scoreTone = (score) => {
  if (score < 60) return 'error';
  if (score < 75) return 'brand';
  if (score < 85) return 'warning';
  return 'success';
};

const ScoreBadge = ({ score }) => (
  <Badge tone={scoreTone(score)}>{(score ?? 0).toFixed(1)}</Badge>
);

const COLUMN_ACCESSORS = {
  driver: (r) =>
    (r.user?.fullName || r.user?.name || r.user?.email || r.key || '').toLowerCase(),
  total_evaluations: (r) => r.total_evaluations ?? 0,
  avg_score: (r) => r.avg_score ?? 0,
  rut: (r) => r.pass_rates?.rut ?? 0,
  receiver: (r) => r.pass_rates?.receiver ?? 0,
  distance: (r) => r.pass_rates?.distance ?? 0,
  timeliness: (r) => r.pass_rates?.timeliness ?? 0,
  images_ai: (r) => r.pass_rates?.images_ai ?? 0,
  late_grace_count: (r) => r.late_grace_count ?? 0,
  late_critical_count: (r) => r.late_critical_count ?? 0,
};

const SortableTh = ({ column, sortBy, sortDir, onSort, className = '', children, title }) => {
  const active = sortBy === column;
  const Icon = !active ? ChevronsUpDown : sortDir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <TH
      className={`cursor-pointer select-none ${className}`}
      onClick={() => onSort(column)}
      title={title}
    >
      <span className={`inline-flex items-center gap-1 ${className.includes('text-right') ? 'justify-end' : ''}`}>
        {children}
        <Icon className={`w-3 h-3 ${active ? 'opacity-90' : 'opacity-40'}`} />
      </span>
    </TH>
  );
};

const DriverTab = ({ filters }) => {
  const { t } = useTranslation('deliveryquality');
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('avg_score');
  const [sortDir, setSortDir] = useState('desc');

  const [drilldown, setDrilldown] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const [aiPkgOpen, setAiPkgOpen] = useState(false);
  const [aiPkgId, setAiPkgId] = useState(null);
  const [aiPkgNum, setAiPkgNum] = useState(null);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir(column === 'driver' ? 'asc' : 'desc');
    }
  };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    deliveryQualityService
      .getByDriver({ ...filters, limit: 100, min_count: 5, sort_by: 'avgScore', sort_dir: 1 })
      .then((r) => alive && setRows(r.data?.data || []))
      .catch((e) => alive && setError(e?.message || 'Error'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [filters]);

  useEffect(() => {
    if (!drilldown) return;
    let alive = true;
    setDrillLoading(true);
    setDrillData(null);
    const dateParams = {
      date_from: filters?.date_from,
      date_to: filters?.date_to,
    };
    Promise.all([
      deliveryQualityService.getDriverBreakdown(drilldown.key, dateParams),
      deliveryQualityService.getTimeline({
        ...filters,
        deliverer_id: drilldown.key,
        granularity: 'day',
      }),
      deliveryQualityService.getDriverHistory(drilldown.key, {
        ...dateParams,
        page: 1,
        limit: 200,
      }),
    ])
      .then(([brk, tl, hist]) => {
        if (!alive) return;
        setDrillData({
          breakdown: brk.data,
          timeline: tl.data?.data || [],
          packages: hist.data?.data || [],
        });
      })
      .catch(() => {})
      .finally(() => alive && setDrillLoading(false));
    return () => {
      alive = false;
    };
  }, [drilldown, filters]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = !q
      ? rows
      : rows.filter((r) => {
          const haystack = [
            r.key,
            r.user?.fullName,
            r.user?.name,
            r.user?.lastName,
            r.user?.email,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return haystack.includes(q);
        });

    const accessor = COLUMN_ACCESSORS[sortBy] || COLUMN_ACCESSORS.avg_score;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...base].sort((a, b) => {
      const va = accessor(a);
      const vb = accessor(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [rows, search, sortBy, sortDir]);

  if (loading) {
    return <CardTableSkeleton rows={10} columns={8} />;
  }
  if (error) {
    return (
      <div className="rounded-lg border border-status-error-soft bg-status-error-soft text-status-error px-4 py-3 text-sm">
        {error}
      </div>
    );
  }
  if (!rows.length) {
    return (
      <div className="rounded-lg border border-border bg-bg-muted text-fg-muted px-4 py-3 text-sm">
        {t('Sin datos para el rango.')}
      </div>
    );
  }

  const renderDriverCell = (r) => {
    const fullName = r.user?.fullName || t('Sin nombre');
    const email = r.user?.email;
    return (
      <>
        <div className="font-semibold text-fg">{fullName}</div>
        {email && <div className="text-xs text-fg-muted">{email}</div>}
        <code className="text-[11px] text-fg-subtle">{r.key || 'sin id'}</code>
      </>
    );
  };

  return (
    <>
      <Card padding="none" className="overflow-hidden">
        <div className="px-4 py-3 border-b border-border bg-bg-muted flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <span className="text-sm font-semibold text-fg">
            {t('Ranking por repartidor ({{n}}) — click para ver detalle', { n: filtered.length })}
          </span>
          <Input
            type="text"
            placeholder={t('Buscar por nombre, correo o ID…')}
            className="w-full sm:max-w-[320px]"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {isMobile ? (
          <div className="p-3 flex flex-col gap-2">
            {filtered.map((r) => (
              <button
                type="button"
                key={r.key || 'sin-driver'}
                onClick={() => setDrilldown(r)}
                className="text-left rounded-lg border border-border p-3 flex flex-col gap-2 hover:bg-bg-muted/50 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">{renderDriverCell(r)}</div>
                  <ScoreBadge score={r.avg_score} />
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-fg-muted">
                  <span>{t('# entregas')}: <strong className="text-fg tabular-nums">{r.total_evaluations}</strong></span>
                  <span>% RUT: <strong className="text-fg tabular-nums">{fmtPct(r.pass_rates?.rut)}</strong></span>
                  <span>{t('% Receptor')}: <strong className="text-fg tabular-nums">{fmtPct(r.pass_rates?.receiver)}</strong></span>
                  <span>{t('% Distancia')}: <strong className="text-fg tabular-nums">{fmtPct(r.pass_rates?.distance)}</strong></span>
                  <span>{t('% Puntualidad')}: <strong className="text-fg tabular-nums">{fmtPct(r.pass_rates?.timeliness)}</strong></span>
                  <span>{t('% Imágenes IA')}: <strong className="text-fg tabular-nums">{fmtPct(r.pass_rates?.images_ai)}</strong></span>
                  <span>{t('Tarde 21–22h')}: <strong className="text-status-warning tabular-nums">{r.late_grace_count ?? 0}</strong></span>
                  <span>{t('Critico >22h')}: <strong className="text-status-error tabular-nums">{r.late_critical_count ?? 0}</strong></span>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="max-h-[600px] overflow-auto">
            <Table>
              <THead className="sticky top-0 z-[1]">
                <TR>
                  <SortableTh column="driver" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>
                    {t('Repartidor')}
                  </SortableTh>
                  <SortableTh column="total_evaluations" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} className="text-right">
                    {t('# entregas')}
                  </SortableTh>
                  <SortableTh column="avg_score" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} className="text-right">
                    {t('Score prom')}
                  </SortableTh>
                  <SortableTh column="rut" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} className="text-right">
                    % RUT
                  </SortableTh>
                  <SortableTh column="receiver" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} className="text-right">
                    {t('% Receptor')}
                  </SortableTh>
                  <SortableTh column="distance" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} className="text-right">
                    {t('% Distancia')}
                  </SortableTh>
                  <SortableTh column="timeliness" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} className="text-right">
                    {t('% Puntualidad')}
                  </SortableTh>
                  <SortableTh
                    column="images_ai" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} className="text-right"
                    title={t('Porcentaje de entregas con verdict ENTREGA VALIDADA por la IA (calidad-ia). Considera etiqueta del paquete + numero de domicilio + receptor/lugar.')}
                  >
                    {t('% Imágenes IA')}
                  </SortableTh>
                  <SortableTh
                    column="late_grace_count" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} className="text-right"
                    title={t('Entregas realizadas entre las 21:00 y las 22:00. Estan fuera del horario de gracia, asi que el score de puntualidad decae linealmente hasta 0 conforme se acerca a las 22:00.')}
                  >
                    {t('Tarde 21–22h')}
                  </SortableTh>
                  <SortableTh
                    column="late_critical_count" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} className="text-right"
                    title={t('Entregas posteriores a las 22:00. El score de puntualidad queda en 0 y cuentan como entrega fuera de plazo total.')}
                  >
                    {t('Critico >22h')}
                  </SortableTh>
                </TR>
              </THead>
              <TBody>
                {filtered.map((r) => (
                  <TR
                    key={r.key || 'sin-driver'}
                    className="cursor-pointer"
                    onClick={() => setDrilldown(r)}
                  >
                    <TD>{renderDriverCell(r)}</TD>
                    <TD className="text-right tabular-nums">{r.total_evaluations}</TD>
                    <TD className="text-right"><ScoreBadge score={r.avg_score} /></TD>
                    <TD className="text-right tabular-nums">{fmtPct(r.pass_rates?.rut)}</TD>
                    <TD className="text-right tabular-nums">{fmtPct(r.pass_rates?.receiver)}</TD>
                    <TD className="text-right tabular-nums">{fmtPct(r.pass_rates?.distance)}</TD>
                    <TD className="text-right tabular-nums">{fmtPct(r.pass_rates?.timeliness)}</TD>
                    <TD className="text-right tabular-nums">{fmtPct(r.pass_rates?.images_ai)}</TD>
                    <TD className="text-right tabular-nums text-status-warning">{r.late_grace_count ?? 0}</TD>
                    <TD className="text-right tabular-nums text-status-error font-semibold">{r.late_critical_count ?? 0}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </div>
        )}
      </Card>

      <Modal
        open={!!drilldown}
        onClose={() => setDrilldown(null)}
        size="lg"
        title={
          <div>
            <div className="text-base font-semibold text-fg">
              {drilldown?.user?.fullName || t('Repartidor sin nombre')}
            </div>
            {drilldown?.user?.email && (
              <div className="text-[13px] text-fg-muted font-normal">{drilldown.user.email}</div>
            )}
            <code className="text-[11px] text-fg-subtle">{drilldown?.key}</code>
          </div>
        }
      >
        {drillLoading || !drillData ? (
          <div className="flex flex-col gap-3 py-2">
            <Skeleton className="h-20 rounded-lg" />
            <Skeleton className="h-40 rounded-lg" />
          </div>
        ) : (
          <>
            <h6 className="mb-3 text-sm font-semibold text-fg">
              {t('Desempeño')}{' '}
              {filters?.date_from || filters?.date_to ? (
                <small className="text-fg-subtle font-normal">
                  · {(filters.date_from || '').slice(0, 10) || '…'} →{' '}
                  {(filters.date_to || '').slice(0, 10) || '…'}
                </small>
              ) : (
                <small className="text-fg-subtle font-normal">· {t('últimos 30 días')}</small>
              )}
            </h6>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
              <div className="border border-border rounded-lg p-2">
                <div className="text-fg-muted text-xs">{t('Score promedio')}</div>
                <div className="text-xl font-bold text-fg tabular-nums">{drillData.breakdown.avg_total_score}</div>
              </div>
              <div className="border border-border rounded-lg p-2">
                <div className="text-fg-muted text-xs">{t('Evaluaciones')}</div>
                <div className="text-xl font-bold text-fg tabular-nums">{drillData.breakdown.total_evaluations}</div>
              </div>
              <div className="border border-border rounded-lg p-2">
                <div className="text-fg-muted text-xs">{t('Top issues')}</div>
                <div className="text-sm text-fg">
                  {(drillData.breakdown.top_issues || [])
                    .map((k) => t(labelForCriterion(k)))
                    .join(', ') || '—'}
                </div>
              </div>
            </div>

            <Table>
              <THead>
                <TR>
                  <TH>{t('Criterio')}</TH>
                  <TH className="text-right">{t('Promedio')}</TH>
                  <TH className="text-right">{t('% aprobación')}</TH>
                </TR>
              </THead>
              <TBody>
                {Object.entries(drillData.breakdown.by_criterion || {}).map(([k, v]) => (
                  <TR key={k}>
                    <TD>{t(labelForCriterion(k))}</TD>
                    <TD className="text-right tabular-nums">{v.avg}</TD>
                    <TD className="text-right tabular-nums">{fmtPct(v.pass_rate)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>

            <div className="mt-3">
              <div className="flex justify-between items-center mb-2">
                <strong className="text-[13px] text-fg">
                  {t('Inspeccionar análisis IA por paquete')}
                </strong>
                <small className="text-fg-subtle flex items-center gap-1">
                  <Info className="w-3 h-3" />
                  {t('Click en un paquete para ver verdict, sub-scores e imágenes.')}
                </small>
              </div>
              <DriverPackagesBuckets
                packages={drillData.packages || []}
                onSelect={(pkgId, followingNumber) => {
                  setAiPkgId(pkgId);
                  setAiPkgNum(followingNumber ?? null);
                  setAiPkgOpen(true);
                }}
              />
            </div>

            <h6 className="mt-3 text-sm font-semibold text-fg">{t('Línea de tiempo')}</h6>
            <div style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={drillData.timeline}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="bucket" tickFormatter={(v) => (v || '').slice(0, 10)} />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Line type="monotone" dataKey="avg_score" stroke="var(--color-primary)" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </Modal>

      <PackageAiDetailModal
        packageId={aiPkgId}
        followingNumber={aiPkgNum}
        isOpen={aiPkgOpen}
        toggle={() => setAiPkgOpen((v) => !v)}
      />
    </>
  );
};

export default DriverTab;
